import React, { useState, useEffect } from 'react';
import { 
  Plus, Edit2, Trash2, Save, X, BookOpen, Video, FileText, 
  ChevronDown, ChevronUp, Clock, Award, HelpCircle,
  Check, AlertCircle, Loader2, RefreshCw, GraduationCap, Upload, Link as LinkIcon
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useLanguage } from '../contexts/LanguageContext';
import AdminPinGuard from '../components/AdminPinGuard';
import ImageUploader from '../components/ImageUploader';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const translations = {
  en: {
    title: 'Course Content Management',
    subtitle: 'Manage training modules, lessons, videos and theoretical content',
    selectCourse: 'Select a course to manage',
    modules: 'Modules',
    addModule: 'Add Module',
    moduleTitle: 'Module Title',
    moduleDuration: 'Required Time (hours)',
    lessons: 'Lessons',
    addLesson: 'Add Lesson',
    lessonTitle: 'Lesson Title',
    contentType: 'Content Type',
    video: 'Video',
    document: 'Document/Template Canva',
    text: 'Text Content',
    videoUrl: 'Video URL (YouTube, Vimeo, or direct link)',
    documentUrl: 'Document URL (Canva, PDF, or image)',
    uploadFile: 'Or upload a file',
    textContent: 'Text Content',
    quiz: 'Module Quiz',
    addQuiz: 'Add Quiz Questions',
    question: 'Question',
    options: 'Answer Options',
    correctAnswer: 'Correct Answer',
    save: 'Save',
    cancel: 'Cancel',
    delete: 'Delete',
    loading: 'Loading...',
    saved: 'Saved successfully!',
    error: 'An error occurred',
    noModules: 'No modules yet. Add your first module.',
    noLessons: 'No lessons in this module.',
    quizInfo: '10 questions required, 90% to pass',
    finalExam: 'Final Exam (30 questions, 100% to pass)'
  },
  fr: {
    title: 'Gestion du Contenu des Formations',
    subtitle: 'Gérer les modules, leçons, vidéos et contenu théorique',
    selectCourse: 'Sélectionnez une formation à gérer',
    modules: 'Modules',
    addModule: 'Ajouter un Module',
    moduleTitle: 'Titre du Module',
    moduleDuration: 'Temps Requis (heures)',
    lessons: 'Leçons',
    addLesson: 'Ajouter une Leçon',
    lessonTitle: 'Titre de la Leçon',
    contentType: 'Type de Contenu',
    video: 'Vidéo',
    document: 'Document/Template Canva',
    text: 'Contenu Texte',
    videoUrl: 'URL de la Vidéo (YouTube, Vimeo, ou lien direct)',
    documentUrl: 'URL du Document (Canva, PDF, ou image)',
    uploadFile: 'Ou télécharger un fichier',
    textContent: 'Contenu Texte',
    quiz: 'Quiz du Module',
    addQuiz: 'Ajouter des Questions',
    question: 'Question',
    options: 'Options de Réponse',
    correctAnswer: 'Réponse Correcte',
    save: 'Enregistrer',
    cancel: 'Annuler',
    delete: 'Supprimer',
    loading: 'Chargement...',
    saved: 'Enregistré avec succès !',
    error: 'Une erreur est survenue',
    noModules: 'Aucun module. Ajoutez votre premier module.',
    noLessons: 'Aucune leçon dans ce module.',
    quizInfo: '10 questions requises, 90% pour réussir',
    finalExam: 'Examen Final (30 questions, 100% pour réussir)'
  },
  pt: {
    title: 'Gestão de Conteúdo dos Cursos',
    subtitle: 'Gerir módulos, lições, vídeos e conteúdo teórico',
    selectCourse: 'Selecione um curso para gerir',
    modules: 'Módulos',
    addModule: 'Adicionar Módulo',
    moduleTitle: 'Título do Módulo',
    moduleDuration: 'Tempo Requerido (horas)',
    lessons: 'Lições',
    addLesson: 'Adicionar Lição',
    lessonTitle: 'Título da Lição',
    contentType: 'Tipo de Conteúdo',
    video: 'Vídeo',
    document: 'Documento/Template Canva',
    text: 'Conteúdo de Texto',
    videoUrl: 'URL do Vídeo (YouTube, Vimeo, ou link direto)',
    documentUrl: 'URL do Documento (Canva, PDF, ou imagem)',
    uploadFile: 'Ou carregar um ficheiro',
    textContent: 'Conteúdo de Texto',
    quiz: 'Quiz do Módulo',
    addQuiz: 'Adicionar Perguntas',
    question: 'Pergunta',
    options: 'Opções de Resposta',
    correctAnswer: 'Resposta Correta',
    save: 'Guardar',
    cancel: 'Cancelar',
    delete: 'Eliminar',
    loading: 'A carregar...',
    saved: 'Guardado com sucesso!',
    error: 'Ocorreu um erro',
    noModules: 'Sem módulos. Adicione o seu primeiro módulo.',
    noLessons: 'Sem lições neste módulo.',
    quizInfo: '10 perguntas necessárias, 90% para passar',
    finalExam: 'Exame Final (30 perguntas, 100% para passar)'
  }
};

const AdminCourseContentPage = () => {
  const { language } = useLanguage();
  const t = translations[language] || translations.en;
  
  const [courses, setCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [courseContent, setCourseContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [expandedModules, setExpandedModules] = useState({});
  const [editingModule, setEditingModule] = useState(null);
  const [editingLesson, setEditingLesson] = useState(null);
  const [editingQuiz, setEditingQuiz] = useState(null);
  
  const adminPin = sessionStorage.getItem('admin_pin') || '';

  useEffect(() => {
    fetchCourses();
  }, []);

  useEffect(() => {
    if (selectedCourse) {
      fetchCourseContent(selectedCourse);
    }
  }, [selectedCourse]);

  const fetchCourses = async () => {
    try {
      const response = await axios.get(`${API}/elearning/courses`);
      setCourses(response.data.courses || []);
    } catch (error) {
      console.error('Error fetching courses:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCourseContent = async (courseId) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin-courses/${courseId}`, {
        headers: { 'X-Admin-Pin': adminPin }
      });
      setCourseContent(response.data);
    } catch (error) {
      console.error('Error fetching course content:', error);
      // Initialize empty structure if course has no content yet
      setCourseContent({
        id: courseId,
        modules: []
      });
    } finally {
      setLoading(false);
    }
  };

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const saveCourseContent = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API}/admin-courses/${selectedCourse}/content`,
        { modules: courseContent.modules },
        { headers: { 'X-Admin-Pin': adminPin } }
      );
      showToast(t.saved);
    } catch (error) {
      console.error('Error saving course content:', error);
      showToast(t.error, 'error');
    } finally {
      setSaving(false);
    }
  };

  const addModule = () => {
    const newModule = {
      id: `module_${Date.now()}`,
      title_en: '',
      title_fr: '',
      title_pt: '',
      required_time_hours: 3,
      lessons: [],
      quiz: { questions: [] }
    };
    setCourseContent(prev => ({
      ...prev,
      modules: [...(prev.modules || []), newModule]
    }));
    setEditingModule(newModule.id);
    setExpandedModules(prev => ({ ...prev, [newModule.id]: true }));
  };

  const updateModule = (moduleId, field, value) => {
    setCourseContent(prev => ({
      ...prev,
      modules: prev.modules.map(m => 
        m.id === moduleId ? { ...m, [field]: value } : m
      )
    }));
  };

  const deleteModule = (moduleId) => {
    if (window.confirm(language === 'fr' ? 'Supprimer ce module ?' : 'Delete this module?')) {
      setCourseContent(prev => ({
        ...prev,
        modules: prev.modules.filter(m => m.id !== moduleId)
      }));
    }
  };

  const addLesson = (moduleId) => {
    const newLesson = {
      id: `lesson_${Date.now()}`,
      title_en: '',
      title_fr: '',
      title_pt: '',
      content_type: 'video',
      content_url: '',
      content: ''
    };
    setCourseContent(prev => ({
      ...prev,
      modules: prev.modules.map(m => 
        m.id === moduleId 
          ? { ...m, lessons: [...(m.lessons || []), newLesson] }
          : m
      )
    }));
    setEditingLesson({ moduleId, lessonId: newLesson.id });
  };

  const updateLesson = (moduleId, lessonId, field, value) => {
    setCourseContent(prev => ({
      ...prev,
      modules: prev.modules.map(m => 
        m.id === moduleId 
          ? { 
              ...m, 
              lessons: m.lessons.map(l => 
                l.id === lessonId ? { ...l, [field]: value } : l
              )
            }
          : m
      )
    }));
  };

  const deleteLesson = (moduleId, lessonId) => {
    if (window.confirm(language === 'fr' ? 'Supprimer cette leçon ?' : 'Delete this lesson?')) {
      setCourseContent(prev => ({
        ...prev,
        modules: prev.modules.map(m => 
          m.id === moduleId 
            ? { ...m, lessons: m.lessons.filter(l => l.id !== lessonId) }
            : m
        )
      }));
    }
  };

  const addQuizQuestion = (moduleId) => {
    const newQuestion = {
      id: `q_${Date.now()}`,
      question: '',
      options: ['', '', '', ''],
      correct_answer: 0
    };
    setCourseContent(prev => ({
      ...prev,
      modules: prev.modules.map(m => 
        m.id === moduleId 
          ? { 
              ...m, 
              quiz: { 
                ...m.quiz, 
                questions: [...(m.quiz?.questions || []), newQuestion] 
              }
            }
          : m
      )
    }));
  };

  const updateQuizQuestion = (moduleId, questionId, field, value) => {
    setCourseContent(prev => ({
      ...prev,
      modules: prev.modules.map(m => 
        m.id === moduleId 
          ? { 
              ...m, 
              quiz: {
                ...m.quiz,
                questions: m.quiz.questions.map(q => 
                  q.id === questionId ? { ...q, [field]: value } : q
                )
              }
            }
          : m
      )
    }));
  };

  const deleteQuizQuestion = (moduleId, questionId) => {
    setCourseContent(prev => ({
      ...prev,
      modules: prev.modules.map(m => 
        m.id === moduleId 
          ? { 
              ...m, 
              quiz: {
                ...m.quiz,
                questions: m.quiz.questions.filter(q => q.id !== questionId)
              }
            }
          : m
      )
    }));
  };

  const getLocalizedTitle = (item) => {
    return item[`title_${language}`] || item.title_en || item.title_fr || '';
  };

  const handleFileUpload = async (file, moduleId, lessonId) => {
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/admin/upload-image`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'X-Admin-Pin': adminPin
        }
      });

      if (response.data.success) {
        const fileUrl = `${process.env.REACT_APP_BACKEND_URL}${response.data.url}`;
        updateLesson(moduleId, lessonId, 'content_url', fileUrl);
        showToast(t.saved);
      }
    } catch (error) {
      console.error('Upload error:', error);
      showToast(t.error, 'error');
    }
  };

  const renderLessonForm = (module, lesson) => {
    const isEditing = editingLesson?.moduleId === module.id && editingLesson?.lessonId === lesson.id;
    
    return (
      <div key={lesson.id} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            {lesson.content_type === 'video' && <Video className="h-4 w-4 text-red-500" />}
            {lesson.content_type === 'document' && <FileText className="h-4 w-4 text-blue-500" />}
            {lesson.content_type === 'text' && <BookOpen className="h-4 w-4 text-green-500" />}
            <input
              type="text"
              value={lesson[`title_${language}`] || lesson.title_fr || ''}
              onChange={(e) => updateLesson(module.id, lesson.id, `title_${language}`, e.target.value)}
              placeholder={t.lessonTitle}
              className="font-medium text-kamit-brown bg-transparent border-b border-transparent hover:border-kamit-brown/30 focus:border-kamit-gold focus:outline-none px-1"
            />
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setEditingLesson(isEditing ? null : { moduleId: module.id, lessonId: lesson.id })}
              className="p-1 text-gray-500 hover:text-kamit-gold"
            >
              {isEditing ? <ChevronUp className="h-4 w-4" /> : <Edit2 className="h-4 w-4" />}
            </button>
            <button
              onClick={() => deleteLesson(module.id, lesson.id)}
              className="p-1 text-gray-500 hover:text-red-500"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        </div>

        {isEditing && (
          <div className="space-y-4 mt-4 pt-4 border-t border-gray-200">
            {/* Content Type Selector */}
            <div>
              <label className="text-sm font-medium text-kamit-brown block mb-2">{t.contentType}</label>
              <div className="flex gap-2">
                {[
                  { type: 'video', icon: Video, label: t.video, color: 'red' },
                  { type: 'document', icon: FileText, label: t.document, color: 'blue' },
                  { type: 'text', icon: BookOpen, label: t.text, color: 'green' }
                ].map(({ type, icon: Icon, label, color }) => (
                  <button
                    key={type}
                    onClick={() => updateLesson(module.id, lesson.id, 'content_type', type)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors ${
                      lesson.content_type === type
                        ? `bg-${color}-100 border-${color}-500 text-${color}-700`
                        : 'bg-white border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <Icon className="h-4 w-4" />
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {/* Video URL Input */}
            {lesson.content_type === 'video' && (
              <div>
                <label className="text-sm font-medium text-kamit-brown block mb-2">
                  <Video className="h-4 w-4 inline mr-1" />
                  {t.videoUrl}
                </label>
                <input
                  type="text"
                  value={lesson.content_url || ''}
                  onChange={(e) => updateLesson(module.id, lesson.id, 'content_url', e.target.value)}
                  placeholder="https://www.youtube.com/watch?v=... ou https://vimeo.com/..."
                  className="w-full p-3 border border-gray-200 rounded-lg focus:border-kamit-gold focus:outline-none"
                />
                <p className="text-xs text-gray-500 mt-1">
                  YouTube, Vimeo, Google Drive, ou lien direct vers fichier vidéo (.mp4)
                </p>
              </div>
            )}

            {/* Document URL/Upload */}
            {lesson.content_type === 'document' && (
              <div className="space-y-3">
                <div>
                  <label className="text-sm font-medium text-kamit-brown block mb-2">
                    <LinkIcon className="h-4 w-4 inline mr-1" />
                    {t.documentUrl}
                  </label>
                  <input
                    type="text"
                    value={lesson.content_url || ''}
                    onChange={(e) => updateLesson(module.id, lesson.id, 'content_url', e.target.value)}
                    placeholder="https://www.canva.com/design/... ou lien PDF"
                    className="w-full p-3 border border-gray-200 rounded-lg focus:border-kamit-gold focus:outline-none"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Canva (lien de partage), Google Docs, PDF, ou image
                  </p>
                </div>
                
                <div className="flex items-center gap-4">
                  <span className="text-sm text-gray-500">{t.uploadFile}</span>
                  <label className="flex items-center gap-2 px-4 py-2 bg-kamit-gold/10 text-kamit-brown rounded-lg cursor-pointer hover:bg-kamit-gold/20 transition-colors">
                    <Upload className="h-4 w-4" />
                    <span className="text-sm">Upload PDF/Image</span>
                    <input
                      type="file"
                      accept=".pdf,.png,.jpg,.jpeg,.webp"
                      onChange={(e) => {
                        if (e.target.files?.[0]) {
                          handleFileUpload(e.target.files[0], module.id, lesson.id);
                        }
                      }}
                      className="hidden"
                    />
                  </label>
                </div>

                {lesson.content_url && (
                  <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                    <p className="text-sm text-green-700 flex items-center gap-2">
                      <Check className="h-4 w-4" />
                      Document lié: <a href={lesson.content_url} target="_blank" rel="noopener noreferrer" className="underline">{lesson.content_url.slice(0, 50)}...</a>
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Text Content */}
            {lesson.content_type === 'text' && (
              <div>
                <label className="text-sm font-medium text-kamit-brown block mb-2">
                  <BookOpen className="h-4 w-4 inline mr-1" />
                  {t.textContent}
                </label>
                <textarea
                  value={lesson.content || ''}
                  onChange={(e) => updateLesson(module.id, lesson.id, 'content', e.target.value)}
                  placeholder="Contenu de la leçon..."
                  rows={6}
                  className="w-full p-3 border border-gray-200 rounded-lg focus:border-kamit-gold focus:outline-none resize-y"
                />
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderQuizForm = (module) => {
    const quiz = module.quiz || { questions: [] };
    const questionsCount = quiz.questions?.length || 0;
    
    return (
      <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <HelpCircle className="h-5 w-5 text-purple-600" />
            <span className="font-medium text-purple-800">{t.quiz}</span>
            <span className="text-sm text-purple-600">({questionsCount}/10 questions)</span>
          </div>
          <button
            onClick={() => addQuizQuestion(module.id)}
            disabled={questionsCount >= 10}
            className="flex items-center gap-1 px-3 py-1 bg-purple-600 text-white rounded-lg text-sm hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Plus className="h-4 w-4" />
            {t.addQuiz}
          </button>
        </div>
        
        <p className="text-xs text-purple-600 mb-4">{t.quizInfo}</p>

        <div className="space-y-4">
          {quiz.questions?.map((q, qIdx) => (
            <div key={q.id} className="bg-white rounded-lg p-4 border border-purple-100">
              <div className="flex items-start justify-between mb-3">
                <span className="text-sm font-medium text-purple-600">Question {qIdx + 1}</span>
                <button
                  onClick={() => deleteQuizQuestion(module.id, q.id)}
                  className="text-gray-400 hover:text-red-500"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
              
              <input
                type="text"
                value={q.question}
                onChange={(e) => updateQuizQuestion(module.id, q.id, 'question', e.target.value)}
                placeholder={t.question}
                className="w-full p-2 border border-gray-200 rounded-lg mb-3 focus:border-purple-500 focus:outline-none"
              />
              
              <div className="space-y-2">
                {q.options.map((opt, optIdx) => (
                  <div key={optIdx} className="flex items-center gap-2">
                    <input
                      type="radio"
                      name={`correct_${q.id}`}
                      checked={q.correct_answer === optIdx}
                      onChange={() => updateQuizQuestion(module.id, q.id, 'correct_answer', optIdx)}
                      className="text-purple-600"
                    />
                    <input
                      type="text"
                      value={opt}
                      onChange={(e) => {
                        const newOptions = [...q.options];
                        newOptions[optIdx] = e.target.value;
                        updateQuizQuestion(module.id, q.id, 'options', newOptions);
                      }}
                      placeholder={`Option ${optIdx + 1}`}
                      className={`flex-1 p-2 border rounded-lg focus:outline-none ${
                        q.correct_answer === optIdx 
                          ? 'border-green-500 bg-green-50' 
                          : 'border-gray-200'
                      }`}
                    />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderModuleCard = (module, index) => {
    const isExpanded = expandedModules[module.id];
    
    return (
      <div key={module.id} className="bg-white rounded-xl shadow-md border border-kamit-brown/10 overflow-hidden">
        {/* Module Header */}
        <div 
          className="p-4 bg-gradient-to-r from-kamit-brown to-kamit-dark text-white cursor-pointer"
          onClick={() => setExpandedModules(prev => ({ ...prev, [module.id]: !prev[module.id] }))}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-kamit-gold rounded-full flex items-center justify-center font-bold">
                {index + 1}
              </div>
              <div>
                <input
                  type="text"
                  value={module[`title_${language}`] || module.title_fr || ''}
                  onChange={(e) => {
                    e.stopPropagation();
                    updateModule(module.id, `title_${language}`, e.target.value);
                  }}
                  onClick={(e) => e.stopPropagation()}
                  placeholder={t.moduleTitle}
                  className="font-heading text-lg bg-transparent border-b border-transparent hover:border-white/30 focus:border-kamit-gold focus:outline-none"
                />
                <div className="flex items-center gap-2 text-sm text-white/70 mt-1">
                  <Clock className="h-4 w-4" />
                  <input
                    type="number"
                    value={module.required_time_hours || 3}
                    onChange={(e) => {
                      e.stopPropagation();
                      updateModule(module.id, 'required_time_hours', parseInt(e.target.value) || 3);
                    }}
                    onClick={(e) => e.stopPropagation()}
                    className="w-12 bg-transparent border-b border-white/30 focus:border-kamit-gold focus:outline-none text-center"
                    min="1"
                  />
                  <span>h minimum</span>
                  <span className="mx-2">|</span>
                  <span>{module.lessons?.length || 0} leçons</span>
                  <span className="mx-2">|</span>
                  <span>{module.quiz?.questions?.length || 0}/10 quiz</span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  deleteModule(module.id);
                }}
                className="p-2 text-white/70 hover:text-red-400"
              >
                <Trash2 className="h-5 w-5" />
              </button>
              {isExpanded ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
            </div>
          </div>
        </div>

        {/* Module Content */}
        {isExpanded && (
          <div className="p-4 space-y-4">
            {/* Lessons */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-medium text-kamit-brown flex items-center gap-2">
                  <BookOpen className="h-4 w-4" />
                  {t.lessons}
                </h4>
                <button
                  onClick={() => addLesson(module.id)}
                  className="flex items-center gap-1 px-3 py-1 bg-kamit-gold text-white rounded-lg text-sm hover:bg-kamit-brown"
                >
                  <Plus className="h-4 w-4" />
                  {t.addLesson}
                </button>
              </div>
              
              {module.lessons?.length > 0 ? (
                <div className="space-y-3">
                  {module.lessons.map(lesson => renderLessonForm(module, lesson))}
                </div>
              ) : (
                <p className="text-sm text-gray-500 italic">{t.noLessons}</p>
              )}
            </div>

            {/* Quiz */}
            {renderQuizForm(module)}
          </div>
        )}
      </div>
    );
  };

  return (
    <AdminPinGuard>
      <div className="min-h-screen bg-gradient-to-br from-stone-50 to-amber-50 py-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-heading text-kamit-brown flex items-center gap-3">
              <GraduationCap className="h-8 w-8 text-kamit-gold" />
              {t.title}
            </h1>
            <p className="text-kamit-brown/60 mt-1">{t.subtitle}</p>
          </div>

          {/* Toast */}
          {toast && (
            <div className={`fixed top-4 right-4 z-50 px-6 py-3 rounded-lg shadow-lg flex items-center gap-2 ${
              toast.type === 'error' ? 'bg-red-500 text-white' : 'bg-green-500 text-white'
            }`}>
              {toast.type === 'error' ? <AlertCircle className="h-5 w-5" /> : <Check className="h-5 w-5" />}
              {toast.message}
            </div>
          )}

          {/* Course Selector */}
          <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
            <label className="text-sm font-medium text-kamit-brown block mb-2">{t.selectCourse}</label>
            <select
              value={selectedCourse || ''}
              onChange={(e) => setSelectedCourse(e.target.value)}
              className="w-full p-3 border border-kamit-brown/20 rounded-lg focus:border-kamit-gold focus:outline-none"
            >
              <option value="">-- {t.selectCourse} --</option>
              {courses.map(course => (
                <option key={course.id} value={course.id}>
                  {course[`title_${language}`] || course.title_fr || course.title_en} - €{course.price}
                </option>
              ))}
            </select>
          </div>

          {/* Course Content Editor */}
          {selectedCourse && (
            <>
              {loading ? (
                <div className="text-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-kamit-gold mx-auto" />
                  <p className="text-kamit-brown/60 mt-2">{t.loading}</p>
                </div>
              ) : (
                <>
                  {/* Action Buttons */}
                  <div className="flex items-center justify-between mb-6">
                    <button
                      onClick={addModule}
                      className="flex items-center gap-2 px-4 py-2 bg-kamit-gold text-white rounded-lg hover:bg-kamit-brown transition-colors"
                    >
                      <Plus className="h-5 w-5" />
                      {t.addModule}
                    </button>
                    
                    <button
                      onClick={saveCourseContent}
                      disabled={saving}
                      className="flex items-center gap-2 px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                    >
                      {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : <Save className="h-5 w-5" />}
                      {t.save}
                    </button>
                  </div>

                  {/* Modules List */}
                  {courseContent?.modules?.length > 0 ? (
                    <div className="space-y-4">
                      {courseContent.modules.map((module, index) => renderModuleCard(module, index))}
                    </div>
                  ) : (
                    <div className="bg-white rounded-xl shadow-lg p-12 text-center">
                      <BookOpen className="h-16 w-16 text-kamit-brown/30 mx-auto mb-4" />
                      <p className="text-kamit-brown/60">{t.noModules}</p>
                      <button
                        onClick={addModule}
                        className="mt-4 flex items-center gap-2 px-4 py-2 bg-kamit-gold text-white rounded-lg hover:bg-kamit-brown transition-colors mx-auto"
                      >
                        <Plus className="h-5 w-5" />
                        {t.addModule}
                      </button>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>
    </AdminPinGuard>
  );
};

export default AdminCourseContentPage;
